/**
 * Metres to drawing units, and back again.
 *
 * `@sts/geometry`'s `scan-plan.ts` puts every wall where it belongs in metres.
 * This is the other half: fitting that into an SVG viewBox, and — the part that
 * has to be exactly right — turning a pair of clicks back into a distance.
 *
 * A hand-placed dimension is only worth anything if the number under it is the
 * real one. Every conversion here is a single uniform scale with a translation
 * (after the drawing's turn, when it has one — see `PlanFit.rotationDeg`),
 * so `toPlan(toView(p)) === p` and a 4.20 m gap measures 4.20 m whatever the
 * drawing is fitted to; `plan-view.test.ts` asserts both rather than trusting
 * the algebra. Aspect ratio is never adjusted independently per axis — a plan
 * stretched 3% wider than it is tall would still look like a plan, and every
 * horizontal dimension on it would be wrong by 3%.
 */

import type { PlanBounds, PlanPoint } from "@sts/geometry";

export interface PlanFit {
  /** The one scale factor, applied to both axes. */
  readonly pixelsPerMetre: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly viewWidth: number;
  readonly viewHeight: number;
  /**
   * The turn the plan is drawn at, degrees from +X toward +Y, applied about
   * the plan origin before the scale — squared to its walls, the front at the
   * bottom (`@sts/geometry` plan-orientation.ts). A turn is still a
   * similarity: lengths and angles measured through `toPlan` are unchanged,
   * and nothing stored is ever in anything but the scan's own frame.
   * Absent: not turned.
   */
  readonly rotationDeg?: number;
}

/** The drawing's turn, in degrees (0 when it has none). */
export function viewTurnDeg(fit: PlanFit): number {
  return fit.rotationDeg ?? 0;
}

/** A direction in plan metres, as the same direction on the drawing. */
export function toViewDirection(v: PlanPoint, fit: PlanFit): PlanPoint {
  const deg = viewTurnDeg(fit);
  if (deg === 0) return v;
  const t = (deg * Math.PI) / 180;
  return { x: v.x * Math.cos(t) - v.y * Math.sin(t), y: v.x * Math.sin(t) + v.y * Math.cos(t) };
}

/** Drawing units kept clear on each side of the fitted plan. */
export interface PlanInsets {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

/**
 * Fits `bounds` inside a viewBox of `viewWidth` x `viewHeight`, keeping
 * `margin` drawing units clear — the same on every side, or per side when the
 * edges carry different furniture (a toolbar along the top, a scale bar along
 * the bottom) — and centred in whichever axis has slack.
 *
 * A degenerate bounding box — one room, one wall, a scan with a single surface
 * — would otherwise divide by zero and produce an infinite scale, so each span
 * is floored at a centimetre.
 */
export function fitPlan(
  bounds: PlanBounds,
  viewWidth: number,
  viewHeight: number,
  margin: number | PlanInsets,
  /** The drawing's turn; `bounds` are then of the plan AS TURNED. */
  rotationDeg = 0,
): PlanFit {
  const insets: PlanInsets =
    typeof margin === "number"
      ? { top: margin, right: margin, bottom: margin, left: margin }
      : margin;
  const spanX = Math.max(bounds.maxX - bounds.minX, 0.01);
  const spanY = Math.max(bounds.maxY - bounds.minY, 0.01);
  const innerWidth = Math.max(viewWidth - insets.left - insets.right, 1);
  const innerHeight = Math.max(viewHeight - insets.top - insets.bottom, 1);

  // ONE factor, the smaller of the two — never a separate one per axis.
  const pixelsPerMetre = Math.min(innerWidth / spanX, innerHeight / spanY);

  return {
    pixelsPerMetre,
    offsetX:
      insets.left + (innerWidth - spanX * pixelsPerMetre) / 2 - bounds.minX * pixelsPerMetre,
    offsetY:
      insets.top + (innerHeight - spanY * pixelsPerMetre) / 2 - bounds.minY * pixelsPerMetre,
    viewWidth,
    viewHeight,
    ...(rotationDeg !== 0 ? { rotationDeg } : {}),
  };
}

/** A point in metres, as a point in the SVG's own coordinates. */
export function toView(point: PlanPoint, fit: PlanFit): PlanPoint {
  const p = toViewDirection(point, fit);
  return {
    x: p.x * fit.pixelsPerMetre + fit.offsetX,
    y: p.y * fit.pixelsPerMetre + fit.offsetY,
  };
}

/** A point in the SVG's own coordinates, back to metres. */
export function toPlan(point: PlanPoint, fit: PlanFit): PlanPoint {
  const turned = {
    x: (point.x - fit.offsetX) / fit.pixelsPerMetre,
    y: (point.y - fit.offsetY) / fit.pixelsPerMetre,
  };
  const deg = viewTurnDeg(fit);
  if (deg === 0) return turned;
  const t = (-deg * Math.PI) / 180;
  return {
    x: turned.x * Math.cos(t) - turned.y * Math.sin(t),
    y: turned.x * Math.sin(t) + turned.y * Math.cos(t),
  };
}

/** A length in drawing units, in metres. */
export function metresFromView(units: number, fit: PlanFit): number {
  return units / fit.pixelsPerMetre;
}

/**
 * Where a browser pointer event lands in the SVG's own coordinates.
 *
 * Done by hand rather than through `SVGSVGElement.getScreenCTM()` because the
 * matrix that method returns is not available in every environment the portal's
 * tests run in, and because the arithmetic for `preserveAspectRatio="xMidYMid
 * meet"` is four lines: one uniform scale, then the letterbox padding on
 * whichever axis has slack.
 *
 * Returns `undefined` for a zero-sized box — a plan that is displayed at no
 * size has no meaningful click position, and inventing one would place a
 * dimension at infinity.
 */
export function viewPointFromClient(
  rect: { readonly left: number; readonly top: number; readonly width: number; readonly height: number },
  clientX: number,
  clientY: number,
  viewWidth: number,
  viewHeight: number,
): PlanPoint | undefined {
  if (!(rect.width > 0) || !(rect.height > 0)) return undefined;
  const scale = Math.min(rect.width / viewWidth, rect.height / viewHeight);
  if (!(scale > 0)) return undefined;
  const padX = (rect.width - viewWidth * scale) / 2;
  const padY = (rect.height - viewHeight * scale) / 2;
  return {
    x: (clientX - rect.left - padX) / scale,
    y: (clientY - rect.top - padY) / scale,
  };
}

/**
 * The surveyor's zoom and pan, on top of the fitted view.
 *
 * Kept as a separate, tiny transform — one uniform scale about the viewBox
 * origin, then a translation — rather than folded into `PlanFit`, so "Fit"
 * is just resetting this to `IDENTITY_ZOOM` and the fitted base never drifts.
 */
export interface PlanZoom {
  readonly scale: number;
  readonly panX: number;
  readonly panY: number;
}

export const IDENTITY_ZOOM: PlanZoom = { scale: 1, panX: 0, panY: 0 };

export const MIN_ZOOM = 0.5;
export const MAX_ZOOM = 8;

/**
 * The fit a zoomed view draws with. Still one uniform scale plus a
 * translation, so every guarantee above — one factor on both axes,
 * `toPlan(toView(p)) === p` — holds at any zoom.
 */
export function applyZoom(fit: PlanFit, zoom: PlanZoom): PlanFit {
  return {
    ...fit,
    pixelsPerMetre: fit.pixelsPerMetre * zoom.scale,
    offsetX: fit.offsetX * zoom.scale + zoom.panX,
    offsetY: fit.offsetY * zoom.scale + zoom.panY,
  };
}

/**
 * Zooms by `factor` keeping the view point `anchor` fixed on screen — the
 * point under the mouse stays under the mouse, which is the only zoom that
 * does not make somebody hunt for the corner they were looking at.
 */
export function zoomAbout(zoom: PlanZoom, factor: number, anchor: PlanPoint): PlanZoom {
  const scale = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom.scale * factor));
  const k = scale / zoom.scale;
  return {
    scale,
    panX: anchor.x - (anchor.x - zoom.panX) * k,
    panY: anchor.y - (anchor.y - zoom.panY) * k,
  };
}

/** Round lengths a scale bar is allowed to be, longest first. */
const SCALE_BAR_CANDIDATES_M: readonly number[] = [10, 5, 2, 1, 0.5, 0.2, 0.1];

/**
 * The longest round length whose bar fits in `maxUnits` of drawing.
 *
 * The same candidate set as `@sts/geometry`'s report renderer, plus 0.2 m and
 * 0.1 m below it: the report is never zoomed, but this plan is, and at 8x a
 * half-metre bar would run off the side of the drawing.
 */
export function scaleBarMetres(fit: PlanFit, maxUnits: number): number {
  for (const candidate of SCALE_BAR_CANDIDATES_M) {
    if (candidate * fit.pixelsPerMetre <= maxUnits) return candidate;
  }
  return SCALE_BAR_CANDIDATES_M[SCALE_BAR_CANDIDATES_M.length - 1] as number;
}
